/**
 * `env(safe-area-inset-*)` in px, MEASURED through one hidden probe element.
 *
 * WHY A PROBE. The canvas route declares `viewportFit: 'cover'`, so on
 * Android 15 and later, and on every notched iPhone, the page runs edge to
 * edge and the gesture bar, the status bar and the notch are all inside the
 * layout viewport. The only way to learn their size from script is CSS:
 * `env()` resolves in a stylesheet, never in JavaScript, and a custom
 * property holding `env()` or `rem` does NOT resolve to px through
 * `getComputedStyle` either. That trap is exactly how `mobileBarPx()` read
 * the bottom navigation 24 px short on the tester's Pixel 8 Pro (RV-034,
 * 2026-09-29): it parsed `--ws-bar-b: 3.5rem` while the bar was `3.5rem +
 * env(safe-area-inset-bottom)` tall. A probe whose PADDING is the four
 * `env()` values, read back as computed padding, comes back in px.
 *
 * One probe per document, created on first use, 0x0, hidden, inert. Reading
 * it is a synchronous `getComputedStyle`, cheap enough to call from the
 * canvas's resize observer. The parser is exported on its own so the
 * arithmetic is a unit test without a layout engine
 * (tests/unit/workspaces/handset-bar-measure.test.ts).
 */

import { ZERO_INSETS, type SafeAreaInsets } from '@/lib/workspaces/handset-present';

export type { SafeAreaInsets };

const PROBE_ATTR = 'data-ws-safe-area-probe';

/** A computed padding string ("24px", "0px", "") to a non-negative px number. */
function px(v: string | undefined | null): number {
  const n = parseFloat(v ?? '');
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** The four insets from a computed style's padding (the probe's, or a test's). */
export function parseSafeAreaInsets(style: {
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
}): SafeAreaInsets {
  return {
    top: px(style.paddingTop),
    right: px(style.paddingRight),
    bottom: px(style.paddingBottom),
    left: px(style.paddingLeft),
  };
}

/** Read the document's safe-area insets now. Zero everywhere `env()` is unsupported or moot. */
export function readSafeAreaInsets(doc: Document | undefined = typeof document !== 'undefined' ? document : undefined): SafeAreaInsets {
  if (!doc?.body || typeof getComputedStyle !== 'function') return ZERO_INSETS;
  let probe = doc.body.querySelector<HTMLElement>(`[${PROBE_ATTR}]`);
  if (!probe) {
    probe = doc.createElement('div');
    probe.setAttribute(PROBE_ATTR, '');
    probe.setAttribute('aria-hidden', 'true');
    // A browser without `env()` drops the padding declaration and the probe
    // reads 0 on every side, which is the right answer there.
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
      'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);';
    doc.body.appendChild(probe);
  }
  try {
    return parseSafeAreaInsets(getComputedStyle(probe));
  } catch {
    return ZERO_INSETS;
  }
}
